import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { completeInviteSetPassword, fetchInvitePreview } from '../../api/invite';
import { StatusBanner } from '../../components/ui';
import AuthShell from './AuthShell';
import { inputClassName, labelClassName } from '../ui/classes';

function InviteSetPasswordPage({ onAuthSuccess }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setStatus({ type: 'idle', message: '' });
      try {
        const payload = await fetchInvitePreview(token);
        if (!cancelled) {
          setPreview(payload);
          if (!payload.valid) {
            const message =
              payload.status === 'expired'
                ? 'This invite link has expired. Ask your admin to resend it.'
                : payload.status === 'used'
                  ? 'This invite link was already used. Sign in with your password.'
                  : 'This invite link is invalid.';
            setStatus({ type: 'error', message });
          }
        }
      } catch (error) {
        if (!cancelled) {
          setPreview({ valid: false, status: 'invalid' });
          setStatus({
            type: 'error',
            message: error.message || 'Unable to validate invite link.',
          });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus({ type: 'idle', message: '' });

    try {
      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters.');
      }
      if (password !== confirmPassword) {
        throw new Error('Password and confirm password must match.');
      }

      const payload = await completeInviteSetPassword({
        token,
        password,
        confirmPassword,
      });

      if (typeof onAuthSuccess === 'function') {
        onAuthSuccess(payload);
      }
      navigate(payload.redirectTo || '/admin/dashboard', { replace: true });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Unable to set password.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthShell
      brandEyebrow="PandaSeller"
      brandTitle="Activate your CRM access."
      brandBody="Create a password for your staff login. This invite link expires 12 hours after it was sent."
    >
      <div className="grid w-full gap-5">
        <header>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sky-700">
            Staff invite
          </p>
          <h2 className="mt-2 text-[1.7rem] font-semibold tracking-tight text-slate-900">
            Set your password
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {preview?.email
              ? `You'll sign in as ${preview.email} after saving a password.`
              : 'Use the link from your invite email to continue.'}
          </p>
        </header>

        {status.type !== 'idle' && status.message ? (
          <StatusBanner status={status} setStatus={setStatus} />
        ) : null}

        {loading ? (
          <p className="text-sm text-slate-500">Checking invite link…</p>
        ) : preview?.valid ? (
          <form className="grid gap-4" onSubmit={handleSubmit}>
            <label className="grid gap-1.5">
              <span className={`${labelClassName} text-[13px]`}>New password</span>
              <div className="relative">
                <input
                  className={`${inputClassName} min-h-11 rounded-xl pr-14`}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  disabled={saving}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500"
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>
            <label className="grid gap-1.5">
              <span className={`${labelClassName} text-[13px]`}>Confirm password</span>
              <input
                className={`${inputClassName} min-h-11 rounded-xl`}
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                disabled={saving}
              />
            </label>
            <button type="submit" className="crm-btn-primary mt-2 min-h-11" disabled={saving}>
              {saving ? 'Saving…' : 'Save password & continue'}
            </button>
          </form>
        ) : (
          <div className="grid gap-3">
            <p className="text-sm text-slate-600">
              {preview?.status === 'used'
                ? 'You can sign in with the password you already created.'
                : 'Ask your admin to open Settings → Assign User and click Resend invite.'}
            </p>
            <Link
              to="/"
              className="crm-btn-secondary inline-flex min-h-11 items-center justify-center no-underline"
            >
              Go to sign in
            </Link>
          </div>
        )}
      </div>
    </AuthShell>
  );
}

export default InviteSetPasswordPage;
